import NumberInput from '@lyra/core/components/Input/NumberInput'
import Notice from '@lyra/core/components/Notice'
import Section from '@lyra/core/components/Section'
import VideoText from '@lyra/core/components/VideoText'
import formatNumber from '@lyra/core/utils/formatNumber'
import { InstrumentType, Ticker } from '@lyra/web/constants/instruments'
import { OrderQuoteParams, SetOrderQuoteParamsForInstrument } from '@lyra/web/constants/order'
import { parseInstrumentName } from '@lyra/web/utils/instruments'
import { countDecimals } from '@lyra/web/utils/number'
import { getOrderMinAmount, getValidOrderAmount } from '@lyra/web/utils/order'
import { useCallback, useRef, useState } from 'react'

import TradeFormCollateralTypeDropdown from './TradeFormCollateralTypeDropdown'

type Props = {
  ticker: Ticker
  rawQuoteParams: OrderQuoteParams
  onChangeQuoteParams: SetOrderQuoteParamsForInstrument
}

const TradeFormAmountInput = ({ ticker, rawQuoteParams, onChangeQuoteParams }: Props) => {
  const instrumentName = rawQuoteParams.instrument_name

  const amountStep = +ticker.amount_step
  const amountDps = countDecimals(amountStep)

  const [isQuoteCollateralAmount, setIsQuoteCollateralAmount] = useState(false)

  const minAmount = getOrderMinAmount(rawQuoteParams, ticker)
  const isLessThanMinAmount = +rawQuoteParams.amount > 0 && +rawQuoteParams.amount < minAmount
  // Default to min
  const amount = Math.max(+rawQuoteParams.amount, minAmount)
  const [rawAmount, setRawAmount] = useState(amount)
  const [isFocused, setIsFocused] = useState(false)
  // Default to show warning if directly linked to close less than 0.01
  const [showMinAmountWarning, setShowMinAmountWarning] = useState(isLessThanMinAmount)
  // Save spot price to a ref as fallback to prevent limit price from jumping
  const initialConversionPrice = useRef(+ticker.index_price)
  const conversionPrice =
    +rawQuoteParams.limit_price !== 0 ? +rawQuoteParams.limit_price : initialConversionPrice.current

  const handleSetAmount = useCallback(
    (newAmount: number, isQuoteCollateralAmount?: boolean) => {
      setRawAmount(newAmount)
      const baseAmount =
        isQuoteCollateralAmount && conversionPrice !== 0
          ? newAmount / conversionPrice
          : getValidOrderAmount(newAmount, rawQuoteParams, ticker)
      setShowMinAmountWarning(newAmount < minAmount)
      // Always quote/order in base amount
      onChangeQuoteParams((params) => ({
        ...params,
        amount: baseAmount.toString(),
      }))
    },
    [conversionPrice, minAmount, onChangeQuoteParams, rawQuoteParams, ticker]
  )

  const handleChangeIsQuoteCollateral = useCallback(
    (newIsQuoteCollatAmount: boolean) => {
      setIsQuoteCollateralAmount((currIsQuoteSize) => {
        if (newIsQuoteCollatAmount && !currIsQuoteSize) {
          // Flipped to quote size
          handleSetAmount(amount * conversionPrice, newIsQuoteCollatAmount)
        } else if (!newIsQuoteCollatAmount && currIsQuoteSize) {
          // Flipped to base size
          handleSetAmount(amount, newIsQuoteCollatAmount)
        }
        return newIsQuoteCollatAmount
      })
    },
    [amount, conversionPrice, handleSetAmount]
  )

  const instrument = parseInstrumentName(instrumentName)
  const isPerp = instrument?.type === InstrumentType.Perps
  const isSpot = instrument?.type === InstrumentType.Spot
  const showCollateralSwitch = isPerp || isSpot

  const quoteCollateralAmount = +rawQuoteParams.amount * conversionPrice
  const isDefaultLimitPrice = amount <= minAmount

  return (
    <Section.YStack>
      <NumberInput
        width="100%"
        value={isFocused ? rawAmount : isQuoteCollateralAmount ? quoteCollateralAmount : amount}
        renderAsPlaceholder={isDefaultLimitPrice}
        onChangeValue={(val) => handleSetAmount(val, isQuoteCollateralAmount)}
        onFocus={() => {
          setShowMinAmountWarning(false)
          setIsFocused(true)
        }}
        onBlur={() => setIsFocused(false)}
        formatValue={(val) =>
          `${formatNumber(val, {
            dps: amountDps,
          })}`
        }
        leftContent={<VideoText color="secondary">Amount</VideoText>}
        textAlign="right"
        rightContent={
          showCollateralSwitch ? (
            <TradeFormCollateralTypeDropdown
              ticker={ticker}
              isHighlighted={isFocused || !isDefaultLimitPrice}
              isQuoteCollateralAmount={isQuoteCollateralAmount}
              onChangeIsQuoteCollateral={handleChangeIsQuoteCollateral}
            />
          ) : null
        }
      />
      {showMinAmountWarning && !isFocused ? (
        <Notice
          status="warning"
          message={`Amount must be greater than ${formatNumber(minAmount, {
            dps: amountDps,
          })}${isPerp ? ` ${ticker?.base_currency}` : ''}`}
          width="100%"
          onClose={() => setShowMinAmountWarning(false)}
        />
      ) : null}
    </Section.YStack>
  )
}

export default TradeFormAmountInput
